<template>
  <a-modal
    :title="title"
    :width="width"
    :visible="visible"
    :maskClosable="maskClosable"
    :confirmLoading="loading"
    :zIndex="zIndex"
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <!-- 条件搜索 -->
    <div class="table-page-search-wrapper">
      <a-form layout="inline">
        <a-row :gutter="48">
          <!-- 自定义筛选条件插槽 -->
          <slot name="searchFields" :query-param="queryParam" />
          <a-col :md="searchColSpan || 18" :sm="24">
            <!-- 默认搜索表单 -->
            <a-form-item :label="searchLabel">
              <a-input v-model="queryParam[searchKey]" :placeholder="searchPlaceholder || '请输入' + searchLabel" allow-clear @pressEnter="handleQuery"/>
            </a-form-item>
          </a-col>
          <a-col :md="6" :sm="24">
            <a-form-item>
              <a-button type="primary" @click="handleQuery">查询</a-button>
              <a-button style="margin-left: 8px" @click="resetQuery">重置</a-button>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <!-- 数据表格 -->
    <a-table
      :columns="tableColumns"
      :row-key="rowKey"
      :data-source="list"
      :loading="loading"
      :pagination="pagination"
      :row-selection="rowSelection"
      @change="handleTableChange"
      :scroll="scroll"
    >
      <!-- 动态插槽 - 接收父组件的自定义渲染 -->
      <template v-for="col in customColumns" :slot="col.slot" slot-scope="text, record">
        <slot :name="col.slot" v-bind:text="text" v-bind:record="record"></slot>
      </template>
    </a-table>
  </a-modal>
</template>

<script>
import DictTag from '@/components/DictTag'

export default {
  name: 'TableSelectModal',
  dicts: ['hospital_level', 'hospital_nature', 'hospital_type'],
  components: {
    DictTag
  },
  props: {
    // 是否显示弹窗
    visible: {
      type: Boolean,
      default: false
    },
    // 弹窗标题
    title: {
      type: String,
      default: '选择数据'
    },
    // 弹窗宽度
    width: {
      type: [String, Number],
      default: 800
    },
    // 是否可以点击遮罩关闭
    maskClosable: {
      type: Boolean,
      default: false
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    },
    // 行的 key
    rowKey: {
      type: String,
      default: 'id'
    },
    // API 方法
    apiMethod: {
      type: Function,
      required: true
    },
    // 表格列配置
    columns: {
      type: Array,
      default: () => []
    },
    // 初始查询参数
    queryParams: {
      type: Object,
      default: () => ({})
    },
    // 分页大小
    pageSize: {
      type: Number,
      default: 10
    },
    // 表格滚动配置
    scroll: {
      type: Object,
      default: () => ({ y: 400 })
    },
    // 搜索占位符
    searchPlaceholder: {
      type: String,
      default: ''
    },
    // 搜索字段标签
    searchLabel: {
      type: String,
      default: '名称'
    },
    // 搜索字段键名
    searchKey: {
      type: String,
      default: 'name'
    },
    // 搜索表单的列跨度
    searchColSpan: {
      type: Number,
      default: 18
    },
    // 数据响应路径，用于提取数据
    dataPath: {
      type: String,
      default: '' // 例如 'data.records' 或留空取 rows
    },
    // 总数路径
    totalPath: {
      type: String,
      default: '' // 例如 'data.total' 或留空取 total
    },
    // 弹窗 z-index，用于嵌套弹窗场景
    zIndex: {
      type: Number,
      default: 1000
    }
  },
  data () {
    return {
      // 查询参数
      queryParam: {
        pageNum: 1,
        pageSize: this.pageSize
      },
      // 表格数据
      list: [],
      // 加载状态
      loading: false,
      // 分页
      pagination: {
        current: 1,
        pageSize: this.pageSize,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 条`
      },
      // 选中的行
      selectedRowKeys: [],
      selectedRows: []
    }
  },
  computed: {
    // 需要自定义渲染的列
    customColumns() {
      const customCols = []
      this.columns.forEach(col => {
        if (col.scopedSlots && col.scopedSlots.customRender) {
          customCols.push({
            slot: col.scopedSlots.customRender
          })
        }
      })
      return customCols
    },
    // 表格列配置 - 添加自定义渲染
    tableColumns() {
      return this.columns.map(col => {
        const newCol = { ...col }
        // 如果列有自定义渲染，且是字典类型的字段，则添加自定义渲染函数
        if (newCol.dataIndex === 'level' || newCol.dataIndex === 'nature' || newCol.dataIndex === 'type') {
          newCol.customRender = (text, record) => {
            const dictType = `hospital_${newCol.dataIndex}`
            const dictList = this.dict.type[dictType] || []
            const dictItem = dictList.find(item => item.value === text)
            return dictItem ? dictItem.label : text
          }
        }
        return newCol
      })
    },
    // 行选择配置
    rowSelection () {
      if (!this.multiple) {
        return {
          type: 'radio',
          selectedRowKeys: this.selectedRowKeys,
          onChange: this.onSelectChange
        }
      } else {
        return {
          selectedRowKeys: this.selectedRowKeys,
          onChange: this.onSelectChange
        }
      }
    }
  },
  watch: {
    visible (val) {
      if (val) {
        // 合并初始查询参数
        this.queryParam = {
          pageNum: 1,
          pageSize: this.pageSize,
          ...this.queryParams
        }
        this.getList()
      } else {
        this.reset()
      }
    },
    // 弹窗打开期间，父组件改变筛选参数时自动重新查询
    queryParams: {
      handler(val) {
        if (this.visible) {
          this.queryParam = {
            ...this.queryParam,
            pageNum: 1,
            pageSize: this.pagination.pageSize,
            ...val
          }
          this.pagination.current = 1
          this.getList()
        }
      },
      deep: true
    }
  },
  methods: {
    // 查询列表
    getList () {
      this.loading = true
      this.apiMethod({
        ...this.queryParam,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }).then(res => {
        // 根据配置的数据路径提取数据
        if (this.dataPath) {
          // 使用路径提取数据，例如 'data.records'
          const paths = this.dataPath.split('.')
          let data = res
          for (const path of paths) {
            data = data ? data[path] : null
          }
          this.list = data || []

          if (this.totalPath) {
            const totalPaths = this.totalPath.split('.')
            let total = res
            for (const path of totalPaths) {
              total = total ? total[path] : null
            }
            this.pagination.total = total || 0
          } else {
            this.pagination.total = res.total || 0
          }
        } else {
          // 兼容旧格式，默认取 rows 和 total
          this.list = res.rows || []
          this.pagination.total = res.total || 0
        }
      }).finally(() => {
        this.loading = false
      })
    },
    // 查询按钮
    handleQuery () {
      this.pagination.current = 1
      this.getList()
    },
    // 重置按钮
    resetQuery () {
      this.queryParam = {
        pageNum: 1,
        pageSize: this.pagination.pageSize,
        ...this.queryParams
      }
      this.pagination.current = 1
      this.getList()
    },
    // 表格变化
    handleTableChange (pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.getList()
    },
    // 选择行
    onSelectChange (selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
    },
    // 确定
    handleOk () {
      if (this.selectedRowKeys.length === 0) {
        this.$message.warning('请选择数据')
        return
      }
      if (!this.multiple) {
        // 单选，返回单个对象
        this.$emit('ok', this.selectedRows[0])
      } else {
        // 多选，返回数组
        this.$emit('ok', this.selectedRows)
      }
    },
    // 取消
    handleCancel () {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    // 重置
    reset () {
      this.selectedRowKeys = []
      this.selectedRows = []
      this.queryParam = {
        pageNum: 1,
        pageSize: this.pagination.pageSize,
        ...this.queryParams
      }
      this.pagination = {
        current: 1,
        pageSize: this.pagination.pageSize,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 条`
      }
    }
  }
}
</script>

<style scoped>
.table-page-search-wrapper {
  margin-bottom: 16px;
}

/* 隐式滚动条样式 - 只在需要时显示 */
::v-deep .ant-table-body {
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
}

/* Chrome/Safari - 隐藏滚动条但在需要时显示 */
::v-deep .ant-table-body::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::v-deep .ant-table-body::-webkit-scrollbar-track {
  background: transparent;
}

::v-deep .ant-table-body::-webkit-scrollbar-thumb {
  background: #d9d9d9;
  border-radius: 3px;
}

::v-deep .ant-table-body::-webkit-scrollbar-thumb:hover {
  background: #bfbfbf;
}

/* 隐藏滚动条，但在内容溢出时可以通过鼠标滚动 */
::v-deep .ant-modal-body {
  overflow: hidden;
}
</style>
